/**
 * Custom hooks for managing HRIS integrations
 * Provides functionality for:
 * - Integration data management and rendering
 * - Pushing candidates to HRIS systems
 * - Form handling for employee creation
 * - Data mapping between ATS and HRIS systems
 */

import { RipplingIcon } from '@/assets/icons/HRIS';
import { useMutation } from '@tanstack/react-query';
import { API_SERVICE } from '@/api/hris-onboarding';
import { useMemo, useCallback } from 'react';
import { HRIS_API_SERVICE } from '@/pages/hris/api';
import { getErrorMessage, handleHrisApiError } from '@/utils/errors';
import { useShowToast } from '@/hooks';
import { useKFlags } from '@/hooks/use-k-flags';
import { useSelector } from 'react-redux';
import BambooHRLogoIcon from '@/assets/icons/HRIS/BambooHRLogoIcon';
import { Darwinbox as DarwinboxIcon } from '@/assets/icons/ats';
import { Icon } from '@chakra-ui/react';
import { convertSchemaToFormConfig } from '@/components/ats/hris/formConfigUtils';
import { DARWINBOX_HRIS_API_SERVICE } from '@/components/ats/hris/darwinbox/api';
import {
  convertDarwinboxSchemaToFormConfig,
  normalizeDarwinboxValuesForSubmit,
} from '@/components/ats/hris/darwinbox/darwinboxFormUtils';

// ==================== CONSTANTS ====================

/**
 * Predefined integration types for non-Kombo integrations
 * These are direct API integrations that don't go through Kombo
 */
const INTEGRATION_TYPES = {
  RIPPLING: 'rippling/rippling_api',
  BAMBOOHR: 'bamboohr/bamboohr_api',
  DARWINBOX: 'darwinbox/darwinbox_api',
};

/**
 * Static configuration for non-Kombo integrations
 * Contains display information and icon components
 */
const INTEGRATION_DATA = {
  [INTEGRATION_TYPES.RIPPLING]: {
    name: 'Rippling',
    type: INTEGRATION_TYPES.RIPPLING,
    icon: RipplingIcon,
    isImageUrl: false, // Uses React component, not URL
  },
  [INTEGRATION_TYPES.BAMBOOHR]: {
    name: 'BambooHR',
    type: INTEGRATION_TYPES.BAMBOOHR,
    icon: BambooHRLogoIcon,
    isImageUrl: false, // Uses React component, not URL
  },
  [INTEGRATION_TYPES.DARWINBOX]: {
    name: 'Darwinbox',
    type: INTEGRATION_TYPES.DARWINBOX,
    icon: DarwinboxIcon,
    isImageUrl: false,
  },
};

// ==================== UTILITY FUNCTIONS ====================

/**
 * Checks if an integration type is a Kombo integration
 * Kombo integrations require different handling (form-based vs direct push)
 * @param {string} type - Integration type identifier
 * @returns {boolean} True if it's a Kombo integration
 */
const isKomboIntegration = (type) => type?.includes('kombo');

/**
 * Checks if an integration type is the Darwinbox integration
 * Darwinbox uses a custom form-fetch + push flow distinct from Kombo and direct
 * @param {string} type - Integration type identifier
 * @returns {boolean} True if it's the Darwinbox integration
 */
const isDarwinboxIntegration = (type) => type === INTEGRATION_TYPES.DARWINBOX;

/**
 * Normalizes integration names for display purposes
 * Removes 'kombo/' prefix and capitalizes the first letter
 * @param {string} value - Raw integration type value
 * @returns {string} Formatted integration name
 */
const normalizeIntegrationName = (value) => {
  if (!value.startsWith('kombo/')) return value;
  const name = value.replace('kombo/', '');
  return name.charAt(0).toUpperCase() + name.slice(1);
};

/**
 * Creates standardized toast messages for different integration actions
 * Ensures consistent messaging across all integration operations
 * @param {string} action - The action being performed (push, create, fetch)
 * @param {string} integrationName - Name of the integration
 * @param {boolean} isSuccess - Whether the action was successful
 * @returns {string} Formatted toast message
 */
const createToastMessage = (action, integrationName, isSuccess = true) => {
  const actionMap = {
    push: isSuccess ? 'pushed to' : 'Failed to push to',
    create: isSuccess ? 'created in' : 'Failed to create employee in',
    fetch: 'Failed to fetch employee form from',
  };

  const baseMessage = actionMap[action] || action;
  return isSuccess
    ? `Candidate ${baseMessage} ${integrationName} successfully`
    : `${baseMessage} ${integrationName}`;
};

/**
 * Generic utility for flattening nested objects into dot-notation keys
 * Recursively traverses object properties and creates flattened key-value pairs
 * Used for processing complex nested data from ATS applications
 * @param {Object} obj - The object to traverse
 * @param {Function} callback - Optional callback function (unused in current implementation)
 * @param {string} prefix - Prefix for keys (used internally for recursion)
 * @returns {Object} Flattened object with hyphen-separated keys
 */
const traverseObject = (obj, callback, prefix = '') => {
  const result = {};

  const traverse = (node, currentPrefix = '') => {
    for (const key in node) {
      const value = node[key];
      // Recursively traverse nested objects, but not arrays
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        traverse(value, `${currentPrefix}${key}-`);
      } else {
        // Store primitive values with flattened key structure
        result[`${currentPrefix}${key}`] = value;
      }
    }
  };

  traverse(obj, prefix);
  return result;
};

/**
 * Centralized API response handler for consistent error/success handling
 * Standardizes toast notifications and callback execution across all API calls
 * @param {Object} response - API response object
 * @param {Function} toast - Toast notification function
 * @param {string} successMessage - Message to show on success
 * @param {string} errorMessage - Fallback error message
 * @param {Function} onSuccess - Callback to execute on success
 * @returns {boolean} True if successful, false if error
 */
const handleApiResponse = (
  response,
  toast,
  successMessage,
  errorMessage,
  onSuccess
) => {
  // Check for API errors in response
  if (response?.errors?.length) {
    toast({
      title:
        getErrorMessage(response, 'integrations', handleHrisApiError) ||
        errorMessage,
      status: 'error',
    });
    return false;
  }

  // Show success message if provided
  if (successMessage) {
    toast({
      title: successMessage,
      status: 'success',
    });
  }

  // Execute success callback if provided
  if (onSuccess) onSuccess();
  return true;
};

// ==================== MAIN HOOKS ====================

/**
 * Main hook for managing integration data and operations
 * Provides utilities for integration metadata, filtering, and icon rendering
 * @returns {Object} Integration management utilities
 */
export const useHrisIntegrations = () => {
  // Get available HRIS tools from Redux store (Kombo integrations)
  const { data: hrisTools, isLoading: isKomboToolsLoading } = useSelector(
    (state) => state.integrations
  );
  const { feDarwinbox } = useKFlags();

  /**
   * Retrieves comprehensive integration data for a given integration type
   * Handles both Kombo integrations (dynamic) and static integrations
   * @param {string} value - Integration type identifier
   * @returns {Object} Integration metadata including name, type, icon, etc.
   */
  const getIntegrationData = useCallback(
    (value) => {
      // Extract ID for Kombo integrations (remove 'kombo/' prefix)
      const id = value.startsWith('kombo/')
        ? value.replace('kombo/', '')
        : value;

      // Check kombo tools first (dynamic integrations from API)
      const integration = hrisTools?.find(
        (tool) => tool.id === id || tool.id === value
      );

      if (integration) {
        return {
          name: integration.label,
          type: `kombo/${integration.id}`,
          icon: integration.icon_url, // URL-based icon
          isImageUrl: true,
        };
      }

      // Darwinbox is gated behind feDarwinbox until rollout
      if (isDarwinboxIntegration(value) && !feDarwinbox) {
        return {
          name: normalizeIntegrationName(value),
          type: value,
          icon: null,
          isImageUrl: false,
        };
      }

      // Check static integration data (predefined integrations)
      if (INTEGRATION_DATA[value]) {
        return INTEGRATION_DATA[value];
      }

      // Fallback for unknown integrations
      return {
        name: normalizeIntegrationName(value),
        type: value,
        icon: null,
        isImageUrl: false,
      };
    },
    [hrisTools, feDarwinbox]
  );

  /**
   * Memoized icon renderer that handles both URL-based and component-based icons
   * Automatically determines rendering method based on icon type
   * @returns {Function} Icon rendering function
   */
  const renderIcon = useMemo(() => {
    return function RenderIntegrationIcon(
      iconData,
      size = { width: '16px', height: '16px' }
    ) {
      if (!iconData?.icon) return null;

      // Render URL-based icons (Kombo integrations) as img elements
      return iconData.isImageUrl ? (
        <img
          src={iconData.icon}
          width={size.width}
          height={size.height}
          alt={iconData.name || 'Integration icon'}
          style={{ objectFit: 'contain' }}
        />
      ) : (
        // Render component-based icons (static integrations) as Chakra Icons
        <Icon as={iconData.icon} width={size.width} height={size.height} />
      );
    };
  }, []);

  /**
   * Maps raw integration data with enriched metadata
   * Combines basic integration info with display data (name, icon, etc.)
   * @param {Array} integrations - Array of raw integration objects
   * @returns {Array} Enhanced integration objects with metadata
   */
  const mapIntegrations = useCallback(
    (integrations = []) => {
      return integrations.map((integration) => ({
        ...integration,
        ...getIntegrationData(integration.type),
      }));
    },
    [getIntegrationData]
  );

  /**
   * Generic integration filtering utility
   * Applies a filter function and enriches results with metadata
   * @param {Array} integrations - Array of integration objects
   * @param {Function} filterFn - Filter predicate function
   * @returns {Array} Filtered and enriched integration objects
   */
  const filterIntegrations = useCallback(
    (integrations = [], filterFn) => {
      return mapIntegrations(integrations.filter(filterFn));
    },
    [mapIntegrations]
  );

  /**
   * Gets all active integrations with enriched metadata
   * Filters for integrations with 'active' status
   * @param {Array} integrations - Array of integration objects
   * @returns {Array} Active integrations with metadata
   */
  const getActiveIntegrations = useCallback(
    (integrations = []) => {
      return filterIntegrations(
        integrations,
        (integration) => integration.status === 'active'
      );
    },
    [filterIntegrations]
  );

  /**
   * Gets active integrations that haven't been pushed to yet
   * Useful for showing available push destinations
   * @param {Array} integrations - Array of integration objects
   * @param {Array} pushedTypes - Array of integration types already pushed to
   * @returns {Array} Unpushed active integrations with names
   */
  const getUnpushedIntegrations = useCallback(
    (integrations = [], pushedTypes = []) => {
      return filterIntegrations(
        integrations,
        (integration) =>
          integration.status === 'active' &&
          !pushedTypes.includes(integration.type) &&
          (feDarwinbox || !isDarwinboxIntegration(integration.type))
      ).filter((integration) => integration.name); // Only include integrations with names
    },
    [filterIntegrations, feDarwinbox]
  );

  return {
    getIntegrationData,
    getActiveIntegrations,
    getUnpushedIntegrations,
    renderIcon,
    hrisTools,
    isKomboToolsLoading,
  };
};

/**
 * Hook for managing HRIS push operations and employee form handling
 * Handles both direct push (non-Kombo) and form-based (Kombo) integrations
 * @param {Object} options - Configuration options
 * @param {Function} options.onSuccess - Callback executed on successful operations
 * @returns {Object} HRIS push utilities and form handling functions
 */
export const usePushToHris = (options = {}) => {
  const toast = useShowToast();
  const { onSuccess } = options;

  // ==================== MUTATIONS ====================

  // Direct push to HRIS (non-Kombo integrations)
  const pushCandidateToHRIS = useMutation({
    mutationFn: HRIS_API_SERVICE.pushApplicationToHris,
  });
  const pushCandidateToHRISAsync = pushCandidateToHRIS.mutateAsync;

  // Fetch employee form schema (Kombo integrations)
  const getEmployeeForm = useMutation({
    mutationFn: API_SERVICE.getEmployeeForm,
  });
  const getEmployeeFormAsync = getEmployeeForm.mutateAsync;

  // Create employee in HRIS (Kombo integrations)
  const createEmployee = useMutation({
    mutationFn: API_SERVICE.createEmployee,
  });
  const createEmployeeAsync = createEmployee.mutateAsync;

  // Fetch Darwinbox employee form schema (flat field list)
  const getDarwinboxEmployeeForm = useMutation({
    mutationFn: DARWINBOX_HRIS_API_SERVICE.getDarwinboxEmployeeForm,
  });
  const getDarwinboxEmployeeFormAsync = getDarwinboxEmployeeForm.mutateAsync;

  // ==================== API UTILITIES ====================

  /**
   * Generic API call wrapper with standardized error handling
   * Provides consistent error/success handling across all HRIS operations
   * @param {Function} mutationFn - The mutation function to execute
   * @param {Object} params - Parameters to pass to the mutation
   * @param {Object} integration - Integration object for error messages
   * @param {string} action - Action type for message generation
   * @returns {boolean|Object} Success status or response data
   */
  const executeApiCall = useCallback(
    async (mutationFn, params, integration, action) => {
      try {
        const response = await mutationFn(params);
        // Async create-employee path (e.g. TriNet): worker processes in background.
        const isPendingCreate =
          action === 'create' && response?.pending === true;
        const successMessage = isPendingCreate
          ? `Adding candidate to ${integration?.name}. This may take a moment.`
          : createToastMessage(action, integration?.name, true);
        const errorMessage = createToastMessage(
          action,
          integration?.name,
          false
        );

        return handleApiResponse(
          response,
          toast,
          successMessage,
          errorMessage,
          onSuccess
        );
      } catch (error) {
        // Surface the BE error code/message (incl. err_adding_to_hris wrappers
        // and field_errors); fall back to the generic action message.
        const data = error?.response?.data;
        const resolved =
          data?.errors?.length || data?.field_errors
            ? getErrorMessage(data, 'integrations', handleHrisApiError)
            : null;
        toast({
          title:
            resolved || createToastMessage(action, integration?.name, false),
          status: 'error',
        });
        return false;
      }
    },
    [toast, onSuccess]
  );

  /**
   * Fetches employee form schema from Kombo integrations
   * Used to get the required fields for creating an employee in the HRIS
   * @param {Object} integration - Integration configuration object
   * @param {Object} application - Application/candidate data
   * @returns {Array|null} Form schema array or null on error
   */
  const fetchEmployeeForm = useCallback(
    async (integration, application) => {
      try {
        const response = await getEmployeeFormAsync({
          params: {
            integration_type: integration?.type,
            ats_application_id: application?.id,
          },
        });

        // Check for API errors in response
        if (response?.errors?.length) {
          toast({
            title:
              getErrorMessage(response, 'integrations', handleHrisApiError) ||
              'Failed to fetch employee form',
            status: 'error',
          });
          return null;
        }

        // Return form schema (object with default_value for prefilling)
        return response?.data?.employee_form || {};
      } catch {
        // Handle network/unexpected errors
        toast({
          title: createToastMessage('fetch', integration?.name, false),
          status: 'error',
        });
        return null;
      }
    },
    [getEmployeeFormAsync, toast]
  );

  // ==================== DATA TRANSFORMATION ====================

  /**
   * Transforms flat form data into nested object structure
   * Converts hyphen-separated keys back into nested object properties
   * Example: 'contact-email' becomes { contact: { email: value } }
   * @param {Object} flatData - Flat object with hyphen-separated keys
   * @returns {Object} Nested object structure
   */
  const transformToNestedObject = useCallback((flatData) => {
    const result = {};

    /**
     * Helper function to check if a value should be included
     * Filters out empty strings, null, undefined, and zero values for numbers
     * @param {any} value - The value to check
     * @returns {boolean} True if value should be included
     */
    const shouldIncludeValue = (value) => {
      // Filter out null and undefined
      if (value === null || value === undefined) return false;

      // Filter out empty strings
      if (typeof value === 'string' && value.trim() === '') return false;

      // Filter out zero values for numbers
      if (typeof value === 'number' && value === 0) return false;

      // For arrays, only include if not empty
      if (Array.isArray(value) && value.length === 0) return false;

      // For objects, recursively check if it has any valid properties
      if (typeof value === 'object' && !Array.isArray(value)) {
        const cleanedObject = cleanObject(value);
        return Object.keys(cleanedObject).length > 0;
      }

      return true;
    };

    /**
     * Recursively clean objects by removing empty/zero values
     * @param {Object} obj - Object to clean
     * @returns {Object} Cleaned object
     */
    const cleanObject = (obj) => {
      const cleaned = {};

      for (const [key, value] of Object.entries(obj)) {
        if (
          typeof value === 'object' &&
          !Array.isArray(value) &&
          value !== null
        ) {
          const cleanedNested = cleanObject(value);
          if (Object.keys(cleanedNested).length > 0) {
            cleaned[key] = cleanedNested;
          }
        } else if (Array.isArray(value)) {
          // For arrays, clean each item and filter out empty objects
          const cleanedArray = value
            .map((item) =>
              typeof item === 'object' && item !== null
                ? cleanObject(item)
                : item
            )
            .filter((item) => shouldIncludeValue(item));

          if (cleanedArray.length > 0) {
            cleaned[key] = cleanedArray;
          }
        } else if (shouldIncludeValue(value)) {
          cleaned[key] = value;
        }
      }

      return cleaned;
    };

    Object.entries(flatData).forEach(([key, value]) => {
      // Skip if value should not be included
      if (!shouldIncludeValue(value)) return;

      if (key.includes('-')) {
        // Split hyphen-separated keys and create nested structure
        const parts = key.split('-');
        let current = result;

        // Navigate/create nested structure
        for (let i = 0; i < parts.length - 1; i++) {
          const part = parts[i];
          if (!current[part]) {
            current[part] = {};
          }
          current = current[part];
        }

        // Set the final value
        current[parts[parts.length - 1]] = value;
      } else {
        // Direct key-value assignment for non-nested keys
        result[key] = value;
      }
    });

    // Clean the final result to remove any empty nested objects
    return cleanObject(result);
  }, []);

  // ==================== CORE OPERATIONS ====================

  /**
   * Main function for pushing candidates to HRIS systems
   * Handles both Kombo (form-based) and direct push integrations
   * @param {Object} integration - Integration configuration
   * @param {Object} application - Application/candidate data
   * @returns {Object|boolean|null} Form data for Kombo, boolean for direct push, null on error
   */
  const pushToHRIS = useCallback(
    async (integration, application) => {
      if (isKomboIntegration(integration?.type)) {
        const formData = await fetchEmployeeForm(integration, application);
        return formData ? { formData } : null;
      }

      if (isDarwinboxIntegration(integration?.type)) {
        try {
          const fields = await getDarwinboxEmployeeFormAsync({
            applicationId: application?.id,
            integrationType: integration?.type,
          });
          if (!Array.isArray(fields) || fields.length === 0) {
            toast({
              title: createToastMessage('fetch', integration?.name, false),
              status: 'error',
            });
            return null;
          }
          return { darwinboxFields: fields };
        } catch {
          toast({
            title: createToastMessage('fetch', integration?.name, false),
            status: 'error',
          });
          return null;
        }
      }

      const result = await executeApiCall(
        pushCandidateToHRISAsync,
        {
          integration_type: integration?.type,
          application_id: application?.id,
        },
        integration,
        'push'
      );

      return result;
    },
    [
      fetchEmployeeForm,
      executeApiCall,
      pushCandidateToHRISAsync,
      getDarwinboxEmployeeFormAsync,
      toast,
    ]
  );

  /**
   * Submits employee form data to create employee in Kombo HRIS
   * Transforms flat form data to nested structure and sends to API
   * @param {Object} integration - Integration configuration
   * @param {string} appId - Application ID
   * @param {Object} formData - Flat form data from user input
   * @returns {boolean} Success status
   */
  const submitEmployeeForm = useCallback(
    async (integration, appId, formData) => {
      const { hris_documents, ...restFormData } = formData;

      if (isDarwinboxIntegration(integration?.type)) {
        const employeeFields = normalizeDarwinboxValuesForSubmit(restFormData);
        const payload = {
          integration_type: integration?.type,
          application_id: appId,
          employee_fields: employeeFields,
        };
        const result = await executeApiCall(
          pushCandidateToHRISAsync,
          payload,
          integration,
          'create'
        );
        return result;
      }

      const nestedFormData = transformToNestedObject(restFormData);

      const payload = {
        integration_type: integration?.type,
        ats_application_id: appId,
        employee_form_response: { properties: nestedFormData },
      };

      if (hris_documents && hris_documents.length > 0) {
        payload.employee_documents = hris_documents;
      }

      const result = await executeApiCall(
        createEmployeeAsync,
        payload,
        integration,
        'create'
      );

      return result;
    },
    [
      createEmployeeAsync,
      pushCandidateToHRISAsync,
      transformToNestedObject,
      executeApiCall,
    ]
  );

  // ==================== DATA MAPPING UTILITIES ====================

  /**
   * Flattens complex application data into a flat key-value structure
   * Extracts data from nested ATS application objects (offers, contact info)
   * @param {Object} obj - Complex application object from ATS
   * @returns {Object} Flattened data with hyphen-separated keys
   */
  const flattenApplicationData = useCallback((obj) => {
    const result = {};

    // Handle offer data from job stages
    if (obj.ats_job_stages?.length > 0) {
      const offerStageData = obj.ats_job_stages.find(
        (stage) => stage.ats_job_milestone?.name === 'Offer'
      );

      // Extract offer details if available
      if (offerStageData?.ats_offers?.[0]) {
        Object.assign(result, traverseObject(offerStageData.ats_offers[0]));
      }
    }

    // Handle contact information
    if (obj.contact && typeof obj.contact === 'object') {
      Object.assign(result, traverseObject(obj.contact));
    }

    return result;
  }, []);

  /**
   * Creates a field mapping function for transforming ATS data to HRIS fields
   * Handles special cases like email mapping and salary rounding
   * @returns {Function} Field mapper function
   */
  const createFieldMapper = useCallback(() => {
    // Define specific mapping strategies for different field types
    const fieldMappers = {
      email: (flatData) => {
        const email = flatData['email'];
        return email && email.trim() ? email.trim() : undefined;
      },
      salary: (flatData) => {
        const salary = flatData['salary'];
        return salary !== undefined && salary !== null && salary !== ''
          ? Math.round(salary)
          : undefined;
      },
      phone_number: (flatData) => {
        const phoneNumber = flatData['phone_number'];
        return phoneNumber && phoneNumber.trim()
          ? phoneNumber.trim()
          : undefined;
      },
      default: (flatData, key) => {
        const value = flatData[key];
        return value !== undefined && value !== null && value !== ''
          ? value
          : undefined;
      },
    };

    // Return mapper function that selects appropriate strategy
    return (key, flatData) => {
      const mapperKey = ['private_email'].includes(key)
        ? 'email'
        : key === 'yearly_salary'
          ? 'salary'
          : key === 'home_phone_number'
            ? 'phone_number'
            : 'default';

      return fieldMappers[mapperKey](flatData, key);
    };
  }, []);

  /**
   * Recursively processes form fields from nested sections
   * @param {Array} fields - Array of fields that may contain nested sections
   * @param {Function} fieldProcessor - Function to process each field
   */
  const processFieldsRecursively = useCallback(
    (initialFields, fieldProcessor) => {
      const process = (fields) => {
        fields?.forEach((field) => {
          // If this field has its own fields array, it's a nested section
          if (field.fields && Array.isArray(field.fields)) {
            // Recursively process nested fields
            process(field.fields);
          } else if (field.unifiedKey) {
            // This is a regular field with a unified key
            fieldProcessor(field);
          }
        });
      };
      process(initialFields);
    },
    []
  );

  /**
   * Extracts default_value from employee form schema (from employee_form_prepopulated API)
   * Recursively traverses schema including nested object properties
   * @param {Object} schema - Raw employee form schema with default_value in fields
   * @param {string} keyPrefix - Prefix for nested field keys (e.g. 'work' for work.site)
   * @returns {Object} Flat object with form field names and default values
   */
  const extractDefaultValuesFromSchema = useCallback(
    (initialSchema, initialKeyPrefix = '') => {
      const defaultValues = {};

      const extract = (schema, keyPrefix = '') => {
        if (!schema || typeof schema !== 'object') {
          return;
        }

        for (const [fieldName, fieldSchema] of Object.entries(schema)) {
          // Skip invalid field schemas
          if (!fieldSchema || typeof fieldSchema !== 'object') {
            continue;
          }

          const fullFieldKey = keyPrefix
            ? `${keyPrefix}-${fieldName}`
            : fieldName;

          // Handle nested object fields recursively
          if (fieldSchema.type === 'object' && fieldSchema.properties) {
            extract(fieldSchema.properties, fullFieldKey);
            continue;
          }

          // Extract default value if it exists
          const hasDefaultValue =
            fieldSchema.default_value !== undefined &&
            fieldSchema.default_value !== null;

          if (hasDefaultValue) {
            defaultValues[fullFieldKey] = fieldSchema.default_value;
          }
        }
      };

      extract(initialSchema, initialKeyPrefix);
      return defaultValues;
    },
    []
  );

  /**
   * Maps ATS application data to HRIS form fields using unified keys
   * Handles field mapping, name splitting, and data transformation
   * @param {Array} formSchema - HRIS form schema with unified keys
   * @param {Object} applicationData - ATS application data
   * @returns {Object} Mapped form data ready for submission
   */
  const mapUnifiedKeyValues = useCallback(
    (formSchema, applicationData) => {
      const flatAppData = flattenApplicationData(applicationData);
      const fieldMapper = createFieldMapper();

      // Track name fields for potential splitting
      const nameFields = {
        first: null,
        last: null,
        hasFirst: false,
        hasLast: false,
      };
      const result = {};

      // Process each form section and recursively handle nested fields
      formSchema.forEach((section) => {
        // Process all fields recursively (including nested ones)
        processFieldsRecursively(section.fields, (field) => {
          // Extract the actual field key (remove prefix if present)
          const key = field.unifiedKey.includes('-')
            ? field.unifiedKey.split('-').pop()
            : field.unifiedKey;

          // Apply field-specific mapping logic
          const value = fieldMapper(key, flatAppData);

          // Skip if data is not available - don't add null, undefined, or empty string values
          if (value !== undefined && value !== null && value !== '') {
            result[field.name] = value;
          }

          // Track name fields for potential splitting
          if (key === 'first_name') {
            nameFields.first = field.name;
            nameFields.hasFirst =
              value !== undefined && value !== null && value !== '';
          } else if (key === 'last_name') {
            nameFields.last = field.name;
            nameFields.hasLast =
              value !== undefined && value !== null && value !== '';
          }
        });
      });

      // Handle name splitting if first/last names are missing but full name exists
      if (
        (nameFields.first || nameFields.last) &&
        (!nameFields.hasFirst || !nameFields.hasLast) &&
        flatAppData.name
      ) {
        const nameParts = flatAppData.name.split(' ');

        if (nameParts.length >= 2) {
          if (nameFields.first && !nameFields.hasFirst) {
            result[nameFields.first] = nameParts[0]; // First part as first name
          }
          if (nameFields.last && !nameFields.hasLast) {
            result[nameFields.last] = nameParts.slice(1).join(' '); // Rest as last name
          }
        }
      }

      return result;
    },
    [flattenApplicationData, createFieldMapper, processFieldsRecursively]
  );

  /**
   * High-level handler for HRIS push operations with callback support
   * Orchestrates the entire push process and handles different integration types
   * @param {Object} integration - Integration configuration
   * @param {Object} application - Application/candidate data
   * @param {Object} callbacks - Callback functions for different outcomes
   * @param {Function} callbacks.onFormReady - Called when Kombo form is ready
   * @param {Function} callbacks.onDirectSuccess - Called when direct push succeeds
   * @param {Function} callbacks.onError - Called on error
   * @returns {Object|boolean} Result of the push operation
   */
  const handlePushToHRIS = useCallback(
    async (integration, application, callbacks = {}) => {
      const { onFormReady, onDirectSuccess, onError } = callbacks;

      try {
        const result = await pushToHRIS(integration, application);

        // Handle Darwinbox (flat-schema form-based)
        if (result && typeof result === 'object' && result.darwinboxFields) {
          const { formConfig, initialValues } =
            convertDarwinboxSchemaToFormConfig(result.darwinboxFields);

          onFormReady?.({
            formSchema: formConfig,
            mappedValues: initialValues,
            integration,
            application,
          });
        }
        // Handle Kombo integrations (form-based)
        else if (result && typeof result === 'object') {
          const formSchema = convertSchemaToFormConfig(result.formData);
          const mappedValues = mapUnifiedKeyValues(formSchema, application);

          const initialValues = {
            ...mappedValues,
            ...extractDefaultValuesFromSchema(result.formData),
          };

          onFormReady?.({
            formSchema,
            mappedValues: initialValues,
            integration,
            application,
          });
        }
        // Handle direct push integrations
        else if (result === true) {
          onDirectSuccess?.();
        }

        return result;
      } catch (error) {
        onError?.(error);
        return false;
      }
    },
    [pushToHRIS, mapUnifiedKeyValues, extractDefaultValuesFromSchema]
  );

  // ==================== RETURN API ====================

  return {
    // Core push operations
    pushToHRIS,
    handlePushToHRIS,
    submitEmployeeForm,

    // Data mapping utilities
    mapUnifiedKeyValues,
    convertSchemaToFormConfig,

    // Loading states
    isPending: pushCandidateToHRIS.isPending || createEmployee.isPending,
    isFetchingForm:
      getEmployeeForm.isPending || getDarwinboxEmployeeForm.isPending,
  };
};

export default useHrisIntegrations;
